import { useState, useEffect, useRef, useCallback } from 'react';

// Simple deep equality function without lodash
const isDeepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) => {
    if (!obj2.hasOwnProperty(key)) return false;

    if (typeof obj1[key] === 'object' && obj1[key] !== null) {
      return isDeepEqual(obj1[key], obj2[key]);
    }

    return obj1[key] === obj2[key];
  });
};

// Simple array comparison
const areArraysEqual = (arr1, arr2) => {
  if (!arr1 || !arr2) return arr1 === arr2;
  if (arr1.length !== arr2.length) return false;

  for (let i = 0; i < arr1.length; i++) {
    // For primitive values
    if (typeof arr1[i] !== 'object' || arr1[i] === null) {
      if (arr1[i] !== arr2[i]) return false;
    }
    // For objects in arrays
    else if (!isDeepEqual(arr1[i], arr2[i])) {
      return false;
    }
  }

  return true;
};

export function useStoreFilters({ storeId, storeArray, dateRanges, checkForGlobalObject }) {
  const [currentDateFilters, setCurrentDateFilters] = useState([]);
  const [comparePeriodFilters, setComparePeriodFilters] = useState([]);
  const [compareYtdFilters, setCompareYtdFilters] = useState([]);
  const [currentYtdFilters, setCurrentYtdFilters] = useState([]);

  // Use refs to track previous values
  const prevStoreIdRef = useRef();
  const prevDateRangesRef = useRef();
  const filterGenerationTimeRef = useRef(0);

  // Memoized filter creation function to avoid recreating filters unnecessarily
  const createDateIndexFilter = useCallback(
    (range, storeFilter) => {
      if (!range || checkForGlobalObject(range)) return null;

      return [
        {
          col: 'dateindex',
          op: '>=',
          val: range.start_year
            ?.toString()
            ?.concat(
              range.start_week <= 9 && range.start_week > 0
                ? '0'.concat(range.start_week)
                : range.start_week
            ),
        },
        {
          col: 'dateindex',
          op: '<=',
          val: range.end_year
            ?.toString()
            ?.concat(
              range.end_week <= 9 && range.end_week > 0
                ? '0'.concat(range.end_week)
                : range.end_week
            ),
        },
        storeFilter,
      ];
    },
    [checkForGlobalObject]
  );

  useEffect(() => {
    // Skip if no store ID is available
    if (!storeId?.length) return;

    // Create hash of inputs to detect changes
    const storeIdHash = JSON.stringify(storeId);
    const dateRangesHash = JSON.stringify(dateRanges);

    // Skip if nothing has changed and we've processed this combination before
    if (
      prevStoreIdRef.current === storeIdHash &&
      prevDateRangesRef.current === dateRangesHash &&
      filterGenerationTimeRef.current > 0
    ) {
      return;
    }

    // Update tracking refs
    prevStoreIdRef.current = storeIdHash;
    prevDateRangesRef.current = dateRangesHash;
    filterGenerationTimeRef.current = Date.now();

    // Create the store filter once for all operations
    const storeFilter = {
      col: 'store_id',
      op: 'in',
      val: storeId,
    };

    try {
      // Safely destructure dateRanges with defaults
      const {
        overallPerformanceRange,
        compareOverallPerformanceRangeTableOne,
        compareOverallPerformanceRangeYTD,
        overallPerformanceYTDRange,
      } = dateRanges || {};

      // Create new filters
      const newCurrentDateFilters = createDateIndexFilter(overallPerformanceRange, storeFilter);
      const newComparePeriodFilters = createDateIndexFilter(
        compareOverallPerformanceRangeTableOne,
        storeFilter
      );
      const newCompareYtdFilters = createDateIndexFilter(
        compareOverallPerformanceRangeYTD,
        storeFilter
      );
      const newCurrentYtdFilters = createDateIndexFilter(overallPerformanceYTDRange, storeFilter);

      // Update states only if they've changed
      if (newCurrentDateFilters && !areArraysEqual(newCurrentDateFilters, currentDateFilters)) {
        setCurrentDateFilters(newCurrentDateFilters);
      }

      if (
        newComparePeriodFilters &&
        !areArraysEqual(newComparePeriodFilters, comparePeriodFilters)
      ) {
        setComparePeriodFilters(newComparePeriodFilters);
      }

      if (newCompareYtdFilters && !areArraysEqual(newCompareYtdFilters, compareYtdFilters)) {
        setCompareYtdFilters(newCompareYtdFilters);
      }

      if (newCurrentYtdFilters && !areArraysEqual(newCurrentYtdFilters, currentYtdFilters)) {
        setCurrentYtdFilters(newCurrentYtdFilters);
      }
    } catch (error) {
      console.error('Error in useStoreFilters:', error);
    }
  }, [
    storeId,
    dateRanges,
    checkForGlobalObject,
    createDateIndexFilter,
    currentDateFilters,
    comparePeriodFilters,
    compareYtdFilters,
    currentYtdFilters,
  ]);

  return {
    currentDateFilters,
    comparePeriodFilters,
    compareYtdFilters,
    currentYtdFilters,
  };
}
